import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faUsers } from '@fortawesome/pro-solid-svg-icons';
import TextButton from '@ssg/common/Components/TextButton';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import EnvironmentVariableContext from '@ssg/common/EnvironmentVariableContext';
import { useMicrosoftAuth } from '@ssg/common/Components/MicrosoftAuth';
import { casePresenceClient } from './casePresenceClient';
import UserContext from '../../UserContext';

interface Props {
	id: string;
}

const ActiveUsersOnCaseIndicator = ({ id }: Props): JSX.Element | null => {
	const [showIndicator, setShowIndicator] = React.useState(true);
	const [isVisible, setIsVisible] = React.useState(false);
	const [activeUsers, setActiveUsers] = React.useState<Array<{ name: string; email: string; adReferenceId?: string }>>([]);
	const envVar = React.useContext(EnvironmentVariableContext);
	const { getTokens } = useMicrosoftAuth();
	const { user: currentUser } = React.useContext(UserContext);
	const heartbeatIntervalRef = React.useRef<number | undefined>(undefined);

	// Stabilize getTokens to prevent unnecessary effect re-runs when parent re-renders
	const getTokensMemo = React.useCallback(getTokens, []);

	React.useEffect(() => {
		if (!id) return;
		setActiveUsers([]);

		let isDisposed = false;
		let unsubscribe: (() => void) | undefined;
		const abortCtrl = new AbortController();

		const clearHeartbeat = () => {
			if (heartbeatIntervalRef.current !== undefined) {
				window.clearInterval(heartbeatIntervalRef.current);
				heartbeatIntervalRef.current = undefined;
			}
		};

		const run = async () => {
			try {
				// Hub mapping is expected at `${baseUrl}/hubs/casePresence`.
				// If the backend is mapped under `/api/hubs/...`, switch to `envVar.baseApiUrl`.
				await casePresenceClient.ensureStarted({ baseUrlOrBaseApiUrl: envVar.baseUrl, getTokens: getTokensMemo });
				await casePresenceClient.joinCase(id);

				if (isDisposed) return;
				unsubscribe = casePresenceClient.onActiveUsersChangedScoped(payload => {
					if (!isDisposed) {
						if (payload.caseId && payload.caseId !== id) {
							return;
						}

						// Filter out current user from the list
						const currentUserAdReferenceId = currentUser?.id;
						const otherUsers = payload.users.filter(user =>
							!currentUserAdReferenceId || user.adReferenceId !== currentUserAdReferenceId,
						);
						setActiveUsers(otherUsers);
					}
				});

				// Start (and keep) heartbeat while viewing this case.
				clearHeartbeat();
				heartbeatIntervalRef.current = window.setInterval(() => {
					casePresenceClient.heartbeat(id, abortCtrl.signal).catch(err => {
						// Ignore AbortError and network errors
						if (err instanceof Error && err.name !== 'AbortError') {
							// Silently ignore other errors during normal operation
						}
					});
				}, 5000);
			} catch {
				// Non-fatal: indicator can simply remain hidden if presence isn't available.
				console.error('Case presence SignalR failed to start');
			}
		};

		run();

		return () => {
			isDisposed = true;
			clearHeartbeat();
			abortCtrl.abort();
			unsubscribe?.();
			casePresenceClient.clearCurrentCase();
			casePresenceClient.stop().catch(() => {
				// Connection stop is non-critical; ignore errors during cleanup
			});
		};
	}, [currentUser?.id, envVar.baseUrl, getTokensMemo, id]);

	React.useEffect(() => {
		if (activeUsers.length > 0 && showIndicator) {
			// Double rAF ensures the element is painted at -translate-y-full before we animate in
			const rafId = requestAnimationFrame(() => {
				requestAnimationFrame(() => setIsVisible(true));
			});
			return () => cancelAnimationFrame(rafId);
		}
	}, [activeUsers.length, showIndicator]);

	const handleClose = () => {
		setIsVisible(false);
		setTimeout(() => setShowIndicator(false), 300);
	};

	if (activeUsers.length === 0 || !showIndicator) return null;
	return (
		<div className={`overflow-hidden transition-all duration-300 ease-in-out ${isVisible ? 'h-6' : 'h-0'}`}>
			<div
				className={`fixed top-0 left-0 w-full bg-orange-calm text-sm text-white py-1 flex flex-row justify-between items-center z-20 transform transition-transform duration-300 ease-in-out ${isVisible ? 'translate-y-0' : '-translate-y-full'
					}`}
			>
				<div />
				<div className="flex flex-row space-x-2 items-center justify-center">
					<FontAwesomeIcon icon={activeUsers.length === 1 ? faUser : faUsers} />
					<p>{activeUsers.map(user => user.name).join(', ')} er også inde på sagen</p>
				</div>
				<div>
					<TextButton icon={faTimes} onClick={handleClose} />
				</div>
			</div>
		</div>
	);
};

export default ActiveUsersOnCaseIndicator;

